import * as React from 'react';
import { Button, type ButtonProps } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useGate } from '@/client/hooks/useGate';

export interface PermissionButtonProps extends ButtonProps {
  /** Permission code required to enable this button. */
  code: string;
  /** Override the tooltip shown when access is denied. */
  deniedTooltip?: React.ReactNode;
}

// A Button that disables itself (with a "Requires …" tooltip) when the user
// lacks `code`; superadmins pass. All other Button props pass through. UX only
// — the backend still enforces the underlying request.
export const PermissionButton = React.forwardRef<
  HTMLButtonElement,
  PermissionButtonProps
>(({ code, deniedTooltip, disabled, ...props }, ref) => {
  const { allowed, isLoading, reason } = useGate(code);
  const blocked = !allowed;

  const button = (
    <Button
      ref={ref}
      disabled={disabled || blocked || isLoading}
      aria-disabled={blocked || undefined}
      {...props}
    />
  );

  if (!blocked) return button;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* A disabled button emits no pointer events, so the tooltip lives on
              a wrapping span that stays hoverable. */}
          <span className="inline-flex cursor-not-allowed">{button}</span>
        </TooltipTrigger>
        <TooltipContent>{deniedTooltip ?? reason}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
});
PermissionButton.displayName = 'PermissionButton';

// Renders `children` only when the user holds `code`, else `fallback` (nothing
// by default). Use to hide whole panels where a disabled control is
// meaningless. Superadmins always see `children`.
export const PermissionGate: React.FC<{
  code: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}> = ({ code, children, fallback = null }) => {
  const { allowed } = useGate(code);
  return <>{allowed ? children : fallback}</>;
};

export default PermissionButton;
